import { createContext, useContext, useMemo } from "react";
import { LoadingStatus } from "src/libs/fetcher";
import useSWR, { SWRResponse } from "swr";
import type {
  Success,
  Failure,
} from "@memento/app/endpoints/feature-flag/list";
import { getLoadingStatus, useUnauthenticatedFetcher } from "src/libs/fetcher";
import { getFeatureFlagsEndpointUrl } from "src/libs/api-endpoint-lib";
import { offlineFeatureFlags } from "src/contexts/offlineFeatureFlags";

type UseFeatureFlagsFromServerProps = {
  isOffline?: boolean;
};

const useFeatureFlagsFromServer = ({
  isOffline = false,
}: UseFeatureFlagsFromServerProps = {}) => {
  const fetcher = useUnauthenticatedFetcher();
  const key = isOffline ? null : getFeatureFlagsEndpointUrl();

  const { data, error, mutate }: SWRResponse<Success, Failure> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  const result = useMemo(() => {
    if (isOffline) {
      return {
        data: offlineFeatureFlags,
        status: LoadingStatus.SUCCESS,
        mutate: () => {},
      };
    } else {
      return {
        data,
        error,
        status: getLoadingStatus(key, data, error),
        mutate,
      };
    }
  }, [data, error, mutate, key, isOffline]);

  return result;
};

type FeatureFlagContextProps = {
  featureFlags: Success["featureFlags"];
  featureConfig: Success["featureConfig"];
  reloadFeatureFlags: () => void;
};

const FeatureFlagContext = createContext<FeatureFlagContextProps | null>(null);

export type FeatureFlagProviderProps = {
  children: React.ReactNode;
  initialValue: Success;
  isOffline?: boolean;
};

export function FeatureFlagProvider({
  children,
  initialValue,
  isOffline,
}: FeatureFlagProviderProps) {
  const useFeatureFlagsRequest = useFeatureFlagsFromServer({ isOffline });

  const value = useMemo(() => {
    if (
      useFeatureFlagsRequest.status === LoadingStatus.LOADING ||
      useFeatureFlagsRequest.status === LoadingStatus.ERROR ||
      !useFeatureFlagsRequest.data
    ) {
      return {
        ...initialValue,
        reloadFeatureFlags: () => {},
      };
    } else {
      return {
        ...useFeatureFlagsRequest.data,
        reloadFeatureFlags: () => useFeatureFlagsRequest.mutate(),
      };
    }
  }, [useFeatureFlagsRequest, initialValue]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  const contextProps = useContext(FeatureFlagContext);

  if (!contextProps) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider."
    );
  }

  return contextProps;
}
